import { useState } from "react";
import {
  signInWithPopup, GoogleAuthProvider,
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../firebase";
import { C } from "../constants";

const input = {
  width: "100%", padding: "11px 14px", borderRadius: 9,
  border: `1px solid ${C.border}`, background: C.bg,
  color: C.text, fontSize: 14, marginBottom: 10, outline: "none",
};

const friendlyError = (code) => ({
  "auth/invalid-credential":    "Invalid email or password.",
  "auth/user-not-found":        "No account found with that email.",
  "auth/wrong-password":        "Incorrect password.",
  "auth/email-already-in-use":  "An account with that email already exists.",
  "auth/weak-password":         "Password must be at least 6 characters.",
  "auth/invalid-email":         "Please enter a valid email address.",
  "auth/popup-closed-by-user":  "Sign-in cancelled.",
  "auth/too-many-requests":     "Too many attempts. Try again later.",
}[code] || "Something went wrong. Please try again.");

export function AuthPage() {
  const [mode, setMode]       = useState("signin");
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const clearError = () => setError("");

  const googleSignIn = async () => {
    setLoading(true);
    clearError();
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (e) {
      setError(friendlyError(e.code));
      setLoading(false);
    }
  };

  const emailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearError();
    try {
      if (mode === "signin") {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
    } catch (e) {
      setError(friendlyError(e.code));
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    if (!email) { setError("Enter your email above first."); return; }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      clearError();
    } catch (e) {
      setError(friendlyError(e.code));
    }
  };

  const switchMode = (next) => { setMode(next); clearError(); setResetSent(false); };

  return (
    <div style={{
      minHeight: "100vh", background: C.bg, color: C.text,
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: 24,
      fontFamily: "'Inter', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');
        * { box-sizing: border-box; }
        input { font-family: inherit; }
        input::placeholder { color: #6a6c6e; }
        input:focus { border-color: ${C.blue} !important; }
      `}</style>

      {/* Logo */}
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 38, fontWeight: 800, letterSpacing: "-0.02em", color: C.blue }}>
        Fulcrum
      </div>
      <div style={{ color: C.muted, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 4, marginBottom: 36 }}>
        Strength &amp; Cardio Tracker
      </div>

      <div style={{
        width: "100%", maxWidth: 360, background: C.surface,
        borderRadius: 16, border: `1px solid ${C.border}`, padding: "32px 28px",
      }}>
        <h2 style={{ margin: "0 0 24px", fontSize: 18, fontWeight: 600, color: C.text }}>
          {mode === "signin" ? "Sign in" : "Create account"}
        </h2>

        {/* Google */}
        <button onClick={googleSignIn} disabled={loading} style={{
          width: "100%", padding: "11px 16px", borderRadius: 10,
          border: `1px solid ${C.border}`, background: C.card,
          color: C.text, fontSize: 14, fontWeight: 500, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          marginBottom: 20, opacity: loading ? 0.6 : 1,
        }}>
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span style={{ color: C.muted, fontSize: 12 }}>or</span>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>

        {/* Email / password */}
        <form onSubmit={emailAuth}>
          <input
            type="email" placeholder="Email" value={email} required
            onChange={e => setEmail(e.target.value)} style={input}
          />
          <input
            type="password" placeholder="Password" value={password} required
            onChange={e => setPassword(e.target.value)}
            style={{ ...input, marginBottom: mode === "signin" ? 4 : 16 }}
          />

          {mode === "signin" && (
            <div style={{ textAlign: "right", marginBottom: 16 }}>
              <button type="button" onClick={forgotPassword} style={{
                background: "none", border: "none", color: C.muted,
                fontSize: 12, cursor: "pointer", padding: 0,
              }}>
                Forgot password?
              </button>
            </div>
          )}

          {resetSent && (
            <div style={{ color: C.green, fontSize: 13, marginBottom: 12 }}>
              Reset email sent — check your inbox.
            </div>
          )}
          {error && (
            <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</div>
          )}

          <button type="submit" disabled={loading} style={{
            width: "100%", padding: "12px", borderRadius: 10, border: "none",
            background: C.blue, color: "#1a1918", fontSize: 14, fontWeight: 700,
            cursor: "pointer", opacity: loading ? 0.7 : 1,
          }}>
            {mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div style={{ marginTop: 20, textAlign: "center", fontSize: 13, color: C.muted }}>
          {mode === "signin" ? (
            <>No account?{" "}
              <button onClick={() => switchMode("signup")} style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 13, fontWeight: 500, padding: 0 }}>
                Sign up
              </button>
            </>
          ) : (
            <>Already have an account?{" "}
              <button onClick={() => switchMode("signin")} style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 13, fontWeight: 500, padding: 0 }}>
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
